import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVideo, faMusic, faPodcast, faImage, faEye, faEyeSlash, faGlobe } from '@fortawesome/free-solid-svg-icons';
import './Media.css';

const Media = () => {
  // State variables for toggle functionality
  const [isMobile, setIsMobile] = useState(false);
  const [showAllVideos, setShowAllVideos] = useState(false);
  const [showAllAudio, setShowAllAudio] = useState(false);
  const [showAllPodcasts, setShowAllPodcasts] = useState(false);
  const [showAllSites, setShowAllSites] = useState(false);
  const [showAllPhotos, setShowAllPhotos] = useState(false);

  // Refs for scrolling
  const videoSectionRef = useRef(null);
  const audioSectionRef = useRef(null);
  const podcastSectionRef = useRef(null);
  const sitesSectionRef = useRef(null);
  const photoSectionRef = useRef(null);

  // Check if mobile view
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    checkIfMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkIfMobile);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Helper function to limit number of cards displayed
  const limitCards = (cards, showAll, isMobile) => {
    if (showAll) return cards;
    return cards.slice(0, isMobile ? 2 : 4);
  };

  // Toggle handlers with scrolling
  const toggleVideos = () => {
    setShowAllVideos(!showAllVideos);
    if (showAllVideos && videoSectionRef.current) {
      const element = videoSectionRef.current;
      const yOffset = -200;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const toggleAudio = () => {
    setShowAllAudio(!showAllAudio);
    if (showAllAudio && audioSectionRef.current) {
      const element = audioSectionRef.current;
      const yOffset = -200;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const togglePodcasts = () => {
    setShowAllPodcasts(!showAllPodcasts);
    if (showAllPodcasts && podcastSectionRef.current) {
      const element = podcastSectionRef.current;
      const yOffset = -200;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const toggleSites = () => {
    setShowAllSites(!showAllSites);
    if (showAllSites && sitesSectionRef.current) {
      const element = sitesSectionRef.current;
      const yOffset = -200;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const togglePhotos = () => {
    setShowAllPhotos(!showAllPhotos);
    if (showAllPhotos && photoSectionRef.current) {
      const element = photoSectionRef.current;
      const yOffset = -200;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  // Sample media content - Remove thumbnail properties to show placeholders
  const videos = [
    { id: 'vid001', title: 'What is the Fullness of Love', thumbnail: '/images/videos/fullness-of-love.jpeg', url: 'https://youtu.be/C216AeQG4Ew' },
    { id: 'vid002', title: 'Vertical and Horizontal Connection', thumbnail: '/images/videos/horizontal-vertical.jpeg', url: 'https://youtu.be/7BBeO_VYdhw' },
    { id: 'vid003', title: 'Humility and Waiting', thumbnail: '/images/videos/humility-and-waiting.jpeg', url: 'https://youtu.be/4szByEEGLpM' },
    { id: 'vid004', title: 'Waiting', thumbnail: '/images/videos/waiting.jpeg', url: 'https://youtu.be/xCq8R1W0MdE' },
    { id: 'vid005', title: 'PRM Media Coming Soon', /* thumbnail: '/images/videos/soul-wounds.jpg', url: 'https://vimeo.com/987654321', description: 'Katie Souza teaches on understanding and healing soul wounds.' */ },
    { id: 'vid006', title: 'PRM Media Coming Soon', /* thumbnail: '/images/videos/conference-authority.jpg', url: 'https://youtube.com/watch?v=jkl012pqr', description: 'Session from the latest PRM conference.' */ }
  ];

  const audioTracks = [
    { id: 1, title: 'Zion\'s Rest | 60 Minute Soaking Worship | SO LONG SHADOWS', thumbnail: '/images/zions-rest.png', url: 'https://youtu.be/mDqHodhYmpg?si=mm7W674Bmh5vrEUe' },
    { id: 2, title: 'SO LONG SHADOWS | Agape (Your Love)', thumbnail: '/images/agape.png', url: 'https://youtu.be/vmb49T7n8qE?si=ZSkiOVSsFdY06Oi5' },
    { id: 3, title: 'SO LONG SHADOWS | LoveDrunk', thumbnail: '/images/lovedrunk.png', url: 'https://youtu.be/sw04BR1tmvw?si=Xoij_VTDHMG2S2U8' },
    { id: 4, title: 'PRM Media Coming Soon', /*thumbnail: '/audio/prayer-music.jpg',*/ url: '#' },
  ];

  const podcasts = [
    { id: 1, title: 'PRM Media Coming Soon', /*thumbnail: '/podcasts/ep1.jpg',*/ url: '#' },
    { id: 2, title: 'PRM Media Coming Soon', /*thumbnail: '/podcasts/katie-interview.jpg',*/ url: '#' },
    { id: 3, title: 'PRM Media Coming Soon', /*thumbnail: '/podcasts/healing.jpg',*/ url: '#' },
    { id: 4, title: 'PRM Media Coming Soon', /*thumbnail: '/podcasts/prayer-strategies.jpg',*/ url: '#' },
    { id: 5, title: 'PRM Media Coming Soon', /*thumbnail: '/podcasts/science-glory.jpg',*/ url: '#' },
  ];

  const sites = [
    { id: 1, title: 'DrunkShirts.com', thumbnail: '/images/drunkshirts.png', url: 'https://drunkshirts.com' },
    { id: 2, title: 'Deep River Church', thumbnail: '/images/deepriver.png', url: 'https://deepriver.church/' },
    { id: 3, title: 'Smoke and Lasers', thumbnail: '/images/smokeandlasers.png', url: 'https://www.etsy.com/shop/SmokeAndLasers?ref=shop-header-name&listing_id=657404911&from_page=listing' },
  ];

  const photoGalleries = [
    { id: 1, title: 'PRM Media Coming Soon', /*thumbnail: '/photos/conf2023-gallery.jpg',*/ url: '#' },
    { id: 2, title: 'PRM Media Coming Soon', /*thumbnail: '/photos/healing-service.jpg',*/ url: '#' },
    { id: 3, title: 'PRM Media Coming Soon', /*thumbnail: '/photos/outreach.jpg',*/ url: '#' },
  ];

  // Filter media for display
  const displayedVideos = limitCards(videos, showAllVideos, isMobile);
  const displayedAudio = limitCards(audioTracks, showAllAudio, isMobile);
  const displayedPodcasts = limitCards(podcasts, showAllPodcasts, isMobile);
  const displayedSites = limitCards(sites, showAllSites, isMobile);
  const displayedPhotos = limitCards(photoGalleries, showAllPhotos, isMobile);

  return (
    <Container fluid className="p-0">
      {/* Media Header */}
      <div className="media-section main-bg py-5">
        <Container>
          <h1 className="section-title">Media</h1>
          <p className="section-subtitle">Explore PRM's in-house media including videos, music and other sites we partner with.</p>
        </Container>
      </div>

      {/* Videos Section */}
      <div ref={videoSectionRef} className="media-section card-bg py-5">
        <Container>
          <Row className="mb-3">
            <Col>
              <h2 className="h3 mb-2">
                <FontAwesomeIcon icon={faVideo} className="me-2" />
                Videos
              </h2>
              <p className="subtitle-text">Experience PRM's teachings, worship, and testimonies</p>
            </Col>
          </Row>
          <Row className="media-gallery">
            {displayedVideos.map(video => (
              <Col key={video.id} xs={6} md={3} className="mb-4">
                <div className="media-card">
                  <a href={video.url} target="_blank" rel="noopener noreferrer">
                    {video.thumbnail ? (
                      <img src={video.thumbnail} alt={video.title} className="media-thumbnail-img" />
                    ) : (
                      <div className="placeholder-media">
                        <FontAwesomeIcon icon={faVideo} className="placeholder-icon" />
                      </div>
                    )}
                    <div className="media-title">{video.title}</div>
                  </a>
                </div>
              </Col>
            ))}
          </Row>
          {videos.length > (isMobile ? 2 : 4) && (
            <Row className="justify-content-center mt-3">
              <Col xs="auto">
                <Button variant="outline-primary" className="more-btn" onClick={toggleVideos}>
                  <FontAwesomeIcon icon={showAllVideos ? faEyeSlash : faEye} className="me-2" />
                  {showAllVideos ? "Show Less" : "Show More"}
                </Button>
              </Col>
            </Row>
          )}
        </Container>
      </div>

      {/* Audio Section */}
      <div ref={audioSectionRef} className="media-section main-bg py-5">
        <Container>
          <Row className="mb-3">
            <Col>
              <h2 className="h3 mb-2">
                <FontAwesomeIcon icon={faMusic} className="me-2" />
                Audio
              </h2>
              <p className="subtitle-text">Worship music and soaking tracks to enhance your prayer time</p>
            </Col>
          </Row>
          <Row className="media-gallery">
            {displayedAudio.map(track => (
              <Col key={track.id} xs={6} md={3} className="mb-4">
                <div className="media-card">
                  <a href={track.url} target="_blank" rel="noopener noreferrer">
                    {track.thumbnail ? (
                      <img src={track.thumbnail} alt={track.title} className="media-thumbnail-img" />
                    ) : (
                      <div className="placeholder-media">
                        <FontAwesomeIcon icon={faMusic} className="placeholder-icon" />
                      </div>
                    )}
                    <div className="media-title">{track.title}</div>
                  </a>
                </div>
              </Col>
            ))}
          </Row>
          {audioTracks.length > (isMobile ? 2 : 4) && (
            <Row className="justify-content-center mt-3">
              <Col xs="auto">
                <Button variant="outline-primary" className="more-btn" onClick={toggleAudio}>
                  <FontAwesomeIcon icon={showAllAudio ? faEyeSlash : faEye} className="me-2" />
                  {showAllAudio ? "Show Less" : "Show More"}
                </Button>
              </Col>
            </Row>
          )}
        </Container>
      </div>

      {/* Podcasts Section */}
      <div ref={podcastSectionRef} className="media-section card-bg py-5">
        <Container>
          <Row className="mb-3">
            <Col>
              <h2 className="h3 mb-2">
                <FontAwesomeIcon icon={faPodcast} className="me-2" />
                Podcasts
              </h2>
              <p className="subtitle-text">Listen to our podcasts exploring glory concepts and interviews</p>
            </Col>
          </Row>
          <Row className="media-gallery">
            {displayedPodcasts.map(podcast => (
              <Col key={podcast.id} xs={6} md={3} className="mb-4">
                <div className="media-card">
                  <a href={podcast.url} target="_blank" rel="noopener noreferrer">
                    {podcast.thumbnail ? (
                      <img src={podcast.thumbnail} alt={podcast.title} className="media-thumbnail-img" />
                    ) : (
                      <div className="placeholder-media">
                        <FontAwesomeIcon icon={faPodcast} className="placeholder-icon" />
                      </div>
                    )}
                    <div className="media-title">{podcast.title}</div>
                  </a>
                </div>
              </Col>
            ))}
          </Row>
          {podcasts.length > (isMobile ? 2 : 4) && (
            <Row className="justify-content-center mt-3">
              <Col xs="auto">
                <Button variant="outline-primary" className="more-btn" onClick={togglePodcasts}>
                  <FontAwesomeIcon icon={showAllPodcasts ? faEyeSlash : faEye} className="me-2" />
                  {showAllPodcasts ? "Show Less" : "Show More"}
                </Button>
              </Col>
            </Row>
          )}
        </Container>
      </div>

      {/* Sites Section */}
      <div ref={sitesSectionRef} className="media-section main-bg py-5">
        <Container>
          <Row className="mb-3">
            <Col>
              <h2 className="h3 mb-2">
                <FontAwesomeIcon icon={faGlobe} className="me-2" />
                Sites
              </h2>
              <p className="subtitle-text">Explore other sites we're part of</p>
            </Col>
          </Row>
          <Row className="media-gallery">
            {displayedSites.map(site => (
              <Col key={site.id} xs={6} md={3} className="mb-4">
                <div className="media-card">
                  <a href={site.url} target="_blank" rel="noopener noreferrer">
                    {site.thumbnail ? (
                      <img src={site.thumbnail} alt={site.title} className="media-thumbnail-img" />
                    ) : (
                      <div className="placeholder-media">
                        <FontAwesomeIcon icon={faGlobe} className="placeholder-icon" />
                      </div>
                    )}
                    <div className="media-title">{site.title}</div>
                  </a>
                </div>
              </Col>
            ))}
          </Row>
          {sites.length > (isMobile ? 2 : 4) && (
            <Row className="justify-content-center mt-3">
              <Col xs="auto">
                <Button variant="outline-primary" className="more-btn" onClick={toggleSites}>
                  <FontAwesomeIcon icon={showAllSites ? faEyeSlash : faEye} className="me-2" />
                  {showAllSites ? "Show Less" : "Show More"}
                </Button>
              </Col>
            </Row>
          )}
        </Container>
      </div>

      {/* Photo Galleries Section */}
      <div ref={photoSectionRef} className="media-section main-bg py-5">
        <Container>
          <Row className="mb-3">
            <Col>
              <h2 className="h3 mb-2">
                <FontAwesomeIcon icon={faImage} className="me-2" />
                Photo Galleries
              </h2>
              <p className="subtitle-text">Browse through our collection of event and ministry photos</p>
            </Col>
          </Row>
          <Row className="media-gallery">
            {displayedPhotos.map(gallery => (
              <Col key={gallery.id} xs={6} md={3} className="mb-4">
                <div className="media-card">
                  <a href={gallery.url} target="_blank" rel="noopener noreferrer">
                    {gallery.thumbnail ? (
                      <img src={gallery.thumbnail} alt={gallery.title} className="media-thumbnail-img" />
                    ) : (
                      <div className="placeholder-media">
                        <FontAwesomeIcon icon={faImage} className="placeholder-icon" />
                      </div>
                    )}
                    <div className="media-title">{gallery.title}</div>
                  </a>
                </div>
              </Col>
            ))}
          </Row>
          {photoGalleries.length > (isMobile ? 2 : 4) && (
            <Row className="justify-content-center mt-3">
              <Col xs="auto">
                <Button variant="outline-primary" className="more-btn" onClick={togglePhotos}>
                  <FontAwesomeIcon icon={showAllPhotos ? faEyeSlash : faEye} className="me-2" />
                  {showAllPhotos ? "Show Less" : "Show More"}
                </Button>
              </Col>
            </Row>
          )}
        </Container>
      </div>
    </Container>
  );
};

export default Media; 